import React, { useState } from 'react';
import styles from './TrueOddsCalculator.module.css';
import {handleBlurPercentage, handleFocus } from '../../../utils/calc-utils';

const TrueOddsCalculator = () => {
  const [calculationType, setCalculationType] = useState('AllOutcomes');
  const [fields, setFields] = useState({
    outcome1: '',
    outcome2: '',
    baseOdds: '',
    overroundPercentage: '',
    additionalOutcomes: [],
  });
  const [result, setResult] = useState('');
  const [isResultVisible, setIsResultVisible] = useState(false);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFields((prevFields) => ({ ...prevFields, [id]: value }));
  };

  const handleAddOutcome = () => {
    if (fields.additionalOutcomes.length < 10) {
      setFields((prevFields) => ({
        ...prevFields,
        additionalOutcomes: [...prevFields.additionalOutcomes, ''],
      }));
    }
  };

  const handleRemoveOutcome = () => {
    if (fields.additionalOutcomes.length > 0) {
      setFields((prevFields) => ({
        ...prevFields,
        additionalOutcomes: prevFields.additionalOutcomes.slice(0, -1),
      }));
    }
  };

  const handleCalculateTrueOdds = () => {
    if (calculationType === 'AllOutcomes') {
      calculateAllOutcomesTrueOdds();
    } else if (calculationType === 'Overround') {
      calculateOverround();
    }
    setIsResultVisible(true); // Show the result box
  };

  const calculateAllOutcomesTrueOdds = () => {
    const outcomes = [parseFloat(fields.outcome1), parseFloat(fields.outcome2), ...fields.additionalOutcomes.map(parseFloat)];
    if (outcomes.some(isNaN)) {
      setResult('Finish entering all your odds details before calculating');
      return;
    }

    let sumInverseOdds = 0;
    outcomes.forEach(odds => {
      sumInverseOdds += 1 / odds;
    });

    const probabilities = outcomes.map(odds => (1 / odds) / sumInverseOdds * 100);
    const trueOdds = probabilities.map(probability => 1 / (probability / 100));
    const overround = (sumInverseOdds - 1) * 100;

    let resultHTML = "<b>True Odds and Probabilities:</b><br>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<br>";
    outcomes.forEach((odds, index) => {
      resultHTML += `<b>Outcome ${index + 1}:</b> has the true odds ${trueOdds[index].toFixed(2)} (${probabilities[index].toFixed(2)}% probability)<br>`;
    });
    resultHTML += `<hr>The overround on this market is <span class="${overround >= 0 ? styles.profitTextRed : styles.profitTextGreen}">${overround.toFixed(2)}%</span>`;

    setResult(resultHTML);
  };

  const calculateOverround = () => {
    const baseOdds = parseFloat(fields.baseOdds);
    const overroundPercentage = parseFloat(fields.overroundPercentage);

    if (isNaN(baseOdds) || isNaN(overroundPercentage)) {
      setResult('Finish entering all your odds details before calculating');
      return;
    }

    const adjustedOdds = baseOdds * (1 + overroundPercentage / 100);
    const probability = (1 / adjustedOdds) * 100;

    setResult(`The adjusted odds are ${adjustedOdds.toFixed(2)}, with a probability of ${probability.toFixed(2)}%`);
  };

  return (
    <div className={styles.trueOddsCalculator}>
      <h1 className={styles.calculatorTitle}>True Odds Calculator</h1>
      <div className={styles.containerWrapper}>
        <div className={styles.container}>
          <div className={styles.box}>
            <span className={styles.label}>Calculation Method:</span>
            <select
              className={styles.dropdown}
              id="calculationType"
              value={calculationType}
              onChange={(e) => setCalculationType(e.target.value)}
            >
              <option value="AllOutcomes">All Outcomes</option>
              <option value="Overround">Overround</option>
            </select>
          </div>
          {calculationType === 'AllOutcomes' ? (
            <div className={styles.allOutcomesFields}>
              <div className={styles.box}>
                <span className={styles.label}>Outcome 1:</span>
                <input
                  className={`${styles.input} ${styles.outcome}`}
                  type="number"
                  step="0.01"
                  id="outcome1"
                  value={fields.outcome1}
                  onChange={handleInputChange}
                  placeholder="Enter odds for Outcome 1"
                />
              </div>
              <div className={styles.box}>
                <span className={styles.label}>Outcome 2:</span>
                <input
                  className={`${styles.input} ${styles.outcome}`}
                  type="number"
                  step="0.01"
                  id="outcome2"
                  value={fields.outcome2}
                  onChange={handleInputChange}
                  placeholder="Enter odds for Outcome 2"
                />
              </div>
              {fields.additionalOutcomes.map((odds, index) => (
                <div className={styles.box} key={index}>
                  <span className={styles.label}>Outcome {index + 3}:</span>
                  <input
                    className={`${styles.input} ${styles.outcome}`}
                    type="number"
                    step="0.01"
                    value={odds}
                    onChange={(e) => {
                      const newOutcomes = [...fields.additionalOutcomes];
                      newOutcomes[index] = e.target.value;
                      setFields((prevFields) => ({
                        ...prevFields,
                        additionalOutcomes: newOutcomes,
                      }));
                    }}
                    placeholder={`Enter odds for Outcome ${index + 3}`}
                  />
                </div>
              ))}
              <div className={`${styles.box} ${styles.buttonContainer}`}>
                <button
                  type="button"
                  onClick={handleAddOutcome}
                  className={`${styles.greyAccentButton} ${fields.additionalOutcomes.length < 10 ? styles.fullWidth : ''}`}
                >
                  Add Another Outcome
                </button>
                {fields.additionalOutcomes.length > 0 && (
                  <button
                    className={`${styles.greyAccentButton} ${styles.buttonBuffer}`}
                    type="button"
                    onClick={handleRemoveOutcome}
                  >
                    Remove Last Outcome
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className={styles.overroundFields}>
              <div className={styles.box}>
                <span className={styles.label}>Base Odds:</span>
                <input
                  className={`${styles.input} ${styles.odds}`}
                  type="number"
                  step="0.01"
                  id="baseOdds"
                  value={fields.baseOdds}
                  onChange={handleInputChange}
                  placeholder="Enter base odds"
                />
              </div>
              <div className={styles.box}>
                <span className={styles.label}>Overround (%):</span>
                <input
                  className={`${styles.input} ${styles.overround}`}
                  type="text"
                  id="overroundPercentage"
                  value={fields.overroundPercentage}
                  onChange={handleInputChange}
                  onBlur={(e) => handleBlurPercentage(e, setFields)}
                  onFocus={(e) => handleFocus(e, setFields)}
                  placeholder="Enter overround percentage"
                />
              </div>
            </div>
          )}
          <button onClick={handleCalculateTrueOdds}>Calculate True Odds</button>
          <div className={`${styles.result} ${isResultVisible ? styles.resultVisible : ''}`} id="resultTrueOdds" dangerouslySetInnerHTML={{ __html: result }}></div>
        </div>
        <div className={styles.howToUse}>
          <h2>How to Use This Calculator</h2>
          <p>
            The True Odds Calculator helps you calculate the true odds of an event based on the provided odds or overround percentage.
          </p>
          <h3>Which mode of the calculator to use?</h3>
          <p>
            <strong>All Outcomes:</strong> This method calculates true odds using all the odds provided for ALL outcomes in an event. 
            Without entering all outcomes, the value will be incorrect. You can add up to 12 outcomes. 
            One application of this method is for horse races where you can find the overround on the market as well what the true odds should be for each horse. 
          </p>
          <p>
            <strong>Overround:</strong> This method is simpler and converts base odds to true odds using a provided overround percentage.
          </p>
          <h3>Explanation of the individual inputs</h3>
          {calculationType === 'AllOutcomes' ? (
            <>
              <p>
                1. <strong>Outcome 1:</strong> Enter the odds for the first outcome.
              </p>
              <p>
                2. <strong>Outcome 2:</strong> Enter the odds for the second outcome.
              </p>
              <p>
                3. <strong>Additional Outcomes:</strong> Use the "Add Another Outcome" button to add more outcomes as needed.
              </p>
            </>
          ) : (
            <>
              <p>
                1. <strong>Base Odds:</strong> Enter the odds provided by the bookie for the outcome.
              </p>
              <p>
                2. <strong>Overround (%):</strong> Enter the overround percentage.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrueOddsCalculator;